import React from "react";
import "./Table.scss";

export interface Column<T> {
  key: string;
  title: string;
  width?: string;
  // 셀 값은 string 키로 인덱싱되어 정적 타입이 불가능한 제네릭 경계라 any 허용
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (value: any, row: T, index: number) => React.ReactNode;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey?: keyof T | ((row: T) => string);
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T, index: number) => string;
}

// 임의 인터페이스(인덱스 시그니처 없는 행 타입)를 허용하기 위한 제네릭 제약이라 any 필요
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function Table<T extends Record<string, any>>({
  columns,
  data = [],
  rowKey = "id",
  onRowClick,
  rowClassName,
}: TableProps<T>) {
  // 클릭 가능 표시와 호출자가 준 행 클래스를 함께 유지한다.
  // rowClassName 이 없거나 빈 문자열을 돌려주면 그 자리를 아예 비워
  // `class=""` 나 앞뒤 공백이 남지 않게 한다.
  const getRowClassName = (row: T, idx: number): string | undefined => {
    const classes = [
      onRowClick ? "table__row--clickable" : "",
      rowClassName ? rowClassName(row, idx) : "",
    ].filter(Boolean);
    return classes.length > 0 ? classes.join(" ") : undefined;
  };

  const getRowKey = (row: T, idx: number): string => {
    if (typeof rowKey === "function") return rowKey(row);
    return row[rowKey as keyof T]?.toString() ?? String(idx);
  };

  return (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={col.width ? { width: col.width } : {}}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="table__empty">
                데이터가 없습니다.
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              // 행 클릭은 **마우스 편의**일 뿐이다. `<tr>` 에 `role="button"`/`tabIndex`/키
              // 핸들러를 달아 행 자체를 버튼으로 만들면 두 가지가 깨진다:
              //  1. 버블링된 keydown 을 `e.target` 확인 없이 받아 **행 안의 진짜 버튼·링크**의
              //     Enter/Space 네이티브 활성화를 `preventDefault` 로 취소한다 (삭제 버튼에
              //     포커스를 두고 Enter → 삭제는 안 되고 행 클릭이 대신 발화했다).
              //  2. `<tr>` 의 row 의미가 사라져 스크린리더의 표 탐색(행 단위 읽기·열 헤더
              //     연결)이 끊긴다 — 셀이 부모 없는 orphan 이 된다.
              // 키보드 동선은 행이 아니라 **셀 안의 실제 `<button>`** 이 맡는다
              // (`UserList` 회원 식별 셀 참조).
              <tr
                key={getRowKey(row, idx)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={getRowClassName(row, idx)}
              >
                {columns.map((col) => (
                  <td key={col.key}>
                    {col.render ? col.render(row[col.key], row, idx) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
